/**
 * Modal confirmation dialog used before any destructive / forced action.
 *
 * The remote-control surface returns 409 with a human-readable
 * `gate.reason` when an action is unsafe; the UI should re-issue with
 * `force=true` only after the operator explicitly confirms via this
 * dialog, naming the consequence.
 */

import React from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import OcppActionButton from './OcppActionButton';
import { OcppTheme } from './OcppTheme';

export interface OcppConfirmDialogProps {
  visible: boolean;
  title: string;
  message: string;
  /** Defaults to "Confirm". */
  confirmLabel?: string;
  /** Defaults to "Cancel". */
  cancelLabel?: string;
  /** Defaults to `'primary'`. Use `'danger'` for forced/destructive actions. */
  confirmVariant?: 'primary' | 'danger';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const OcppConfirmDialog: React.FC<OcppConfirmDialogProps> = ({
  visible,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  loading = false,
  onConfirm,
  onCancel,
}) => {
  return (
    <Modal
      transparent
      animationType="fade"
      visible={visible}
      onRequestClose={onCancel}
    >
      <View style={styles.overlay}>
        <View style={styles.dialog}>
          <View style={styles.header}>
            <MaterialIcons
              name={confirmVariant === 'danger' ? 'warning' : 'help-outline'}
              size={28}
              color={confirmVariant === 'danger' ? OcppTheme.danger : OcppTheme.accent}
            />
            <Text style={styles.title}>{title}</Text>
          </View>
          <Text style={styles.message}>{message}</Text>
          <View style={styles.actions}>
            <Pressable
              onPress={onCancel}
              style={({ pressed }) => [
                styles.cancel,
                { opacity: pressed ? 0.8 : 1 },
              ]}
              accessibilityRole="button"
            >
              <Text style={styles.cancelText}>{cancelLabel}</Text>
            </Pressable>
            <OcppActionButton
              label={confirmLabel}
              onPress={onConfirm}
              variant={confirmVariant}
              loading={loading}
              accessibilityLabel={confirmLabel}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default OcppConfirmDialog;

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  dialog: {
    width: '100%',
    maxWidth: 380,
    backgroundColor: OcppTheme.surface,
    borderRadius: 16,
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },
  title: {
    flex: 1,
    color: OcppTheme.textPrimary,
    fontSize: 18,
    fontWeight: '700',
  },
  message: {
    color: OcppTheme.textSecondary,
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 20,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 10,
  },
  cancel: {
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  cancelText: {
    color: OcppTheme.textMuted,
    fontSize: 13,
    fontWeight: '600',
  },
});
